import {
  ChevronLeft,
  Shield,
  Users,
  Lock,
  Eye,
  Edit,
  Trash,
  Plus,
  Check,
  X,
  AlertCircle,
  Settings,
  RefreshCw,
  UserPlus,
  Building,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useOrganizationsList } from "../../api/generated_hooks/generated-organizations-hooks";
import { SearchableSelect } from "../../components/SearchableSelect";
import {
  usePermissionsList,
  useCreatePermissions,
  useUpdatePermissions,
  useDeletePermissions,
} from "../../api/generated_hooks/generated-permissions-hooks";
import {
  useRolePermissionsList,
  useCreateRolePermissions,
  useDeleteRolePermissions,
} from "../../api/generated_hooks/generated-role_permissions-hooks";
import {
  useRolesList,
  useCreateRoles,
  useUpdateRoles,
  useDeleteRoles,
} from "../../api/generated_hooks/generated-roles-hooks";
import {
  useUserOrganizationRolesList,
  useCreateUserOrganizationRoles,
  useDeleteUserOrganizationRoles,
} from "../../api/generated_hooks/generated-user_organization_roles-hooks";
import { useUserProfilesList } from "../../api/generated_hooks/generated-user_profiles-hooks";
import { notify } from "../../utils/notifications";

const PermissionMatrixSettings: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("roles");
  const [isAddingRole, setIsAddingRole] = useState(false);
  const [isAddingPermission, setIsAddingPermission] = useState(false);
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [editingPermission, setEditingPermission] = useState<string | null>(
    null,
  );
  const [selectedOrganization, setSelectedOrganization] = useState<
    string | null
  >(null);

  // Fetch data from database
  const {
    data: roles,
    isLoading: rolesLoading,
    refetch: refetchRoles,
  } = useRolesList({
    sortBy: "name",
    sortOrder: "asc",
  });

  const {
    data: permissions,
    isLoading: permissionsLoading,
    refetch: refetchPermissions,
  } = usePermissionsList({
    sortBy: "perm_name",
    sortOrder: "asc",
  });

  const {
    data: rolePermissions,
    isLoading: rolePermissionsLoading,
    refetch: refetchRolePermissions,
  } = useRolePermissionsList();

  const {
    data: users,
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useUserProfilesList({
    sortBy: "full_name",
    sortOrder: "asc",
  });

  const { data: organizations } = useOrganizationsList({
    sortBy: "name",
    sortOrder: "asc",
  });

  const {
    data: userOrgRoles,
    isLoading: userOrgRolesLoading,
    refetch: refetchUserOrgRoles,
  } = useUserOrganizationRolesList();

  // Mutations
  const createRole = useCreateRoles();
  const updateRole = useUpdateRoles();
  const deleteRole = useDeleteRoles();

  const createPermission = useCreatePermissions();
  const updatePermission = useUpdatePermissions();
  const deletePermission = useDeletePermissions();

  const createRolePermission = useCreateRolePermissions();
  const deleteRolePermission = useDeleteRolePermissions();

  const createUserOrgRole = useCreateUserOrganizationRoles();
  const deleteUserOrgRole = useDeleteUserOrganizationRoles();

  // Helper function to check if a role has a permission
  const roleHasPermission = (roleId: string, permissionId: string): boolean => {
    return (
      rolePermissions?.some(
        (rp) => rp.role_id === roleId && rp.permission_id === permissionId,
      ) || false
    );
  };

  // Helper function to check if user has a specific role in an organization
  const userHasRoleInOrg = (
    userId: string,
    orgId: string,
    roleId: string,
  ): boolean => {
    return (
      userOrgRoles?.some(
        (uor) =>
          uor.user_id === userId &&
          uor.organization_id === orgId &&
          uor.role_id === roleId,
      ) || false
    );
  };

  // Helper function to toggle permission for a role
  const toggleRolePermission = async (roleId: string, permissionId: string) => {
    const existing = rolePermissions?.find(
      (rp) => rp.role_id === roleId && rp.permission_id === permissionId,
    );

    try {
      if (existing) {
        await deleteRolePermission.mutateAsync(existing.id);
        notify.success("Permission removed");
      } else {
        // Only send the required FK pair. Fastify's body validation runs
        // Ajv with type coercion, so sending null for the optional uuid /
        // text columns ends up as `""` on the wire (String is first in the
        // generated `Type.Union([Type.String(), Type.Null()])`), which
        // Postgres rejects with "invalid input syntax for type uuid".
        // The DB has defaults for `id`/`created_at` and a trigger
        // (populate_role_permissions_trigger_fields) auto-fills the trig_*
        // columns from the joined role/permission rows.
        await createRolePermission.mutateAsync({
          role_id: roleId,
          permission_id: permissionId,
        } as any);
        notify.success("Permission granted");
      }
      refetchRolePermissions();
    } catch (error) {
      notify.error("Failed to update permission");
    }
  };

  // Helper function to toggle user role in organization
  const toggleUserRole = async (
    userId: string,
    orgId: string,
    roleId: string,
  ) => {
    const existing = userOrgRoles?.find(
      (uor) =>
        uor.user_id === userId &&
        uor.organization_id === orgId &&
        uor.role_id === roleId,
    );

    try {
      if (existing) {
        // Note: user_organization_roles has composite primary key, might need special handling
        await deleteUserOrgRole.mutateAsync({
          user_id: userId,
          organization_id: orgId,
          role_id: roleId,
        } as any);
        notify.success("Role removed from user");
      } else {
        // Same null-coercion gotcha as toggleRolePermission above — omit
        // the optional uuid / text columns and let the DB defaults +
        // populate_user_organization_roles_trigger_fields trigger fill
        // them in.
        await createUserOrgRole.mutateAsync({
          user_id: userId,
          organization_id: orgId,
          role_id: roleId,
        } as any);
        notify.success("Role assigned to user");
      }
      refetchUserOrgRoles();
    } catch (error) {
      notify.error("Failed to update user role");
    }
  };

  // Handle role operations
  const handleCreateRole = async (data: {
    name: string;
    description?: string;
  }) => {
    try {
      await createRole.mutateAsync({
        name: data.name,
        description: data.description || null,
        organization_id: null,
        is_default: null,
      });
      notify.success("Role created successfully");
      setIsAddingRole(false);
      refetchRoles();
    } catch (error) {
      notify.error("Failed to create role");
    }
  };

  const handleUpdateRole = async (
    id: string,
    data: { name: string; description?: string },
  ) => {
    try {
      await updateRole.mutateAsync({
        id,
        data: {
          name: data.name,
          description: data.description || null,
          organization_id: null,
          is_default: null,
        },
      });
      notify.success("Role updated successfully");
      setEditingRole(null);
      refetchRoles();
    } catch (error) {
      notify.error("Failed to update role");
    }
  };

  const handleDeleteRole = async (id: string) => {
    if (
      confirm(
        "Are you sure you want to delete this role? This will remove all associated permissions.",
      )
    ) {
      try {
        await deleteRole.mutateAsync(id);
        notify.success("Role deleted successfully");
        refetchRoles();
        refetchRolePermissions();
      } catch (error) {
        notify.error("Failed to delete role");
      }
    }
  };

  // Handle permission operations
  const handleCreatePermission = async (data: {
    perm_name: string;
    description?: string;
  }) => {
    try {
      await createPermission.mutateAsync({
        perm_name: data.perm_name,
        description: data.description || null,
      });
      notify.success("Permission created successfully");
      setIsAddingPermission(false);
      refetchPermissions();
    } catch (error) {
      notify.error("Failed to create permission");
    }
  };

  const handleUpdatePermission = async (
    id: string,
    data: { perm_name: string; description?: string },
  ) => {
    try {
      await updatePermission.mutateAsync({
        id,
        data: {
          perm_name: data.perm_name,
          description: data.description || null,
        },
      });
      notify.success("Permission updated successfully");
      setEditingPermission(null);
      refetchPermissions();
    } catch (error) {
      notify.error("Failed to update permission");
    }
  };

  const handleDeletePermission = async (id: string) => {
    if (
      confirm(
        "Are you sure you want to delete this permission? This will remove it from all roles.",
      )
    ) {
      try {
        await deletePermission.mutateAsync(id);
        notify.success("Permission deleted successfully");
        refetchPermissions();
        refetchRolePermissions();
      } catch (error) {
        notify.error("Failed to delete permission");
      }
    }
  };

  const tabs = [
    { id: "roles", label: "Role Definitions", icon: Users },
    { id: "permissions", label: "Permission Definitions", icon: Lock },
    { id: "matrix", label: "Permission Matrix", icon: Shield },
    { id: "users", label: "User Roles", icon: UserPlus },
    { id: "features", label: "Feature Access", icon: Settings },
    { id: "data", label: "Data Visibility", icon: Eye },
  ];

  const renderRolesTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Role Definitions</h3>
        <div className="flex gap-2">
          <button
            onClick={() => refetchRoles()}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => setIsAddingRole(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Role
          </button>
        </div>
      </div>

      {rolesLoading ? (
        <div className="text-center py-8">Loading roles...</div>
      ) : (
        <div className="grid gap-4">
          {isAddingRole && (
            <div className="bg-white border-2 border-blue-400 rounded-lg p-4">
              <RoleForm
                onSubmit={handleCreateRole}
                onCancel={() => setIsAddingRole(false)}
              />
            </div>
          )}

          {roles?.map((role) => (
            <div
              key={role.id}
              className="bg-white border border-gray-200 rounded-lg p-4"
            >
              {editingRole === role.id ? (
                <RoleForm
                  initialData={role}
                  onSubmit={(data) => handleUpdateRole(role.id, data)}
                  onCancel={() => setEditingRole(null)}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      ROLE
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{role.name}</h4>
                      <p className="text-sm text-gray-500">
                        {role.description || "No description"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingRole(role.id)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteRole(role.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {(!roles || roles.length === 0) && !isAddingRole && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Roles Defined
              </h3>
              <p className="text-sm text-gray-500">
                Create your first role to get started
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderPermissionsTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Permission Definitions</h3>
        <div className="flex gap-2">
          <button
            onClick={() => refetchPermissions()}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => setIsAddingPermission(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Permission
          </button>
        </div>
      </div>

      {permissionsLoading ? (
        <div className="text-center py-8">Loading permissions...</div>
      ) : (
        <div className="grid gap-4">
          {isAddingPermission && (
            <div className="bg-white border-2 border-blue-400 rounded-lg p-4">
              <PermissionForm
                onSubmit={handleCreatePermission}
                onCancel={() => setIsAddingPermission(false)}
              />
            </div>
          )}

          {permissions?.map((permission) => (
            <div
              key={permission.id}
              className="bg-white border border-gray-200 rounded-lg p-4"
            >
              {editingPermission === permission.id ? (
                <PermissionForm
                  initialData={permission}
                  onSubmit={(data) =>
                    handleUpdatePermission(permission.id, data)
                  }
                  onCancel={() => setEditingPermission(null)}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      PERMISSION
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {permission.perm_name}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {permission.description || "No description"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingPermission(permission.id)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePermission(permission.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {(!permissions || permissions.length === 0) &&
            !isAddingPermission && (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Lock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Permissions Defined
                </h3>
                <p className="text-sm text-gray-500">
                  Create your first permission to get started
                </p>
              </div>
            )}
        </div>
      )}
    </div>
  );

  const renderMatrixTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Permission Matrix</h3>
        <button
          onClick={() => {
            refetchRoles();
            refetchPermissions();
            refetchRolePermissions();
          }}
          className="p-2 text-gray-400 hover:text-gray-600"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {rolesLoading || permissionsLoading || rolePermissionsLoading ? (
        <div className="text-center py-8">Loading permission matrix...</div>
      ) : (
        <>
          {!roles ||
          roles.length === 0 ||
          !permissions ||
          permissions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Setup Required
              </h3>
              <p className="text-sm text-gray-500">
                Please create at least one role and one permission to view the
                matrix
              </p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                      Permission
                    </th>
                    {roles?.map((role) => (
                      <th
                        key={role.id}
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]"
                      >
                        {role.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {permissions?.map((permission) => (
                    <tr key={permission.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 sticky left-0 bg-white z-10">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {permission.perm_name}
                          </div>
                          {permission.description && (
                            <div className="text-xs text-gray-500">
                              {permission.description}
                            </div>
                          )}
                        </div>
                      </td>
                      {roles?.map((role) => (
                        <td
                          key={`${role.id}-${permission.id}`}
                          className="px-6 py-4 text-center"
                        >
                          <button
                            onClick={() =>
                              toggleRolePermission(role.id, permission.id)
                            }
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            disabled={
                              createRolePermission.isPending ||
                              deleteRolePermission.isPending
                            }
                          >
                            {roleHasPermission(role.id, permission.id) ? (
                              <Check className="h-5 w-5 text-green-500" />
                            ) : (
                              <X className="h-5 w-5 text-red-500" />
                            )}
                          </button>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderUsersTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">User Role Assignments</h3>
        <button
          onClick={() => {
            refetchUsers();
            refetchUserOrgRoles();
          }}
          className="p-2 text-gray-400 hover:text-gray-600"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Organization Selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Organization
        </label>
        <SearchableSelect
          value={selectedOrganization}
          onChange={(next) => setSelectedOrganization(next)}
          options={(organizations ?? []).map((org) => ({
            value: org.id,
            label: org.name,
          }))}
          placeholder="-- Select an Organization --"
          searchPlaceholder="Search organizations…"
          clearable
        />
      </div>

      {selectedOrganization ? (
        <>
          {usersLoading || rolesLoading || userOrgRolesLoading ? (
            <div className="text-center py-8">
              Loading user role assignments...
            </div>
          ) : (
            <>
              {!users || users.length === 0 || !roles || roles.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Setup Required
                  </h3>
                  <p className="text-sm text-gray-500">
                    Please create at least one user and one role to manage
                    assignments
                  </p>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                          User
                        </th>
                        {roles?.map((role) => (
                          <th
                            key={role.id}
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]"
                          >
                            {role.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {users?.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 sticky left-0 bg-white z-10">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {user.full_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          </td>
                          {roles?.map((role) => (
                            <td
                              key={`${user.id}-${role.id}`}
                              className="px-6 py-4 text-center"
                            >
                              <button
                                onClick={() =>
                                  toggleUserRole(
                                    user.id,
                                    selectedOrganization,
                                    role.id,
                                  )
                                }
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                disabled={
                                  createUserOrgRole.isPending ||
                                  deleteUserOrgRole.isPending
                                }
                              >
                                {userHasRoleInOrg(
                                  user.id,
                                  selectedOrganization,
                                  role.id,
                                ) ? (
                                  <Check className="h-5 w-5 text-green-500" />
                                ) : (
                                  <X className="h-5 w-5 text-red-500" />
                                )}
                              </button>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Select an Organization
          </h3>
          <p className="text-sm text-gray-500">
            Please select an organization to manage user role assignments
          </p>
        </div>
      )}
    </div>
  );

  const renderFeaturesTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Feature Access Controls</h3>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900">Coming Soon</h4>
            <p className="text-sm text-yellow-700 mt-1">
              Feature-based access control will allow you to define which roles
              can access specific application features and modules.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDataTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Data Visibility Rules</h3>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Eye className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">Field-Level Security</h4>
            <p className="text-sm text-blue-700 mt-1">
              Data visibility rules will control which fields and records users
              can view based on their roles and organizational hierarchy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "roles":
        return renderRolesTab();
      case "permissions":
        return renderPermissionsTab();
      case "matrix":
        return renderMatrixTab();
      case "users":
        return renderUsersTab();
      case "features":
        return renderFeaturesTab();
      case "data":
        return renderDataTab();
      default:
        return renderRolesTab();
    }
  };

  return (
    <div className="p-6">
      {/* Header with back button */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/admin/analytics")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back to Admin</span>
        </button>

        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-red-500 text-white">
            <Shield className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Security & Permissions
            </h1>
            <p className="text-gray-600">
              Manage roles, permissions, and access controls for your
              application
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2
                  ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }
                `}
              >
                <IconComponent className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-gray-50 rounded-lg p-6">{renderTabContent()}</div>
    </div>
  );
};

// Role Form Component
const RoleForm: React.FC<{
  initialData?: { name: string; description?: string | null };
  onSubmit: (data: { name: string; description?: string }) => void;
  onCancel: () => void;
}> = ({ initialData, onSubmit, onCancel }) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(
    initialData?.description || "",
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Role Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., Administrator, Manager, User"
          autoFocus
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Describe this role's purpose and responsibilities"
          rows={2}
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
          disabled={!name.trim()}
        >
          {initialData ? "Update" : "Create"} Role
        </button>
      </div>
    </form>
  );
};

// Permission Form Component
const PermissionForm: React.FC<{
  initialData?: { perm_name: string; description?: string | null };
  onSubmit: (data: { perm_name: string; description?: string }) => void;
  onCancel: () => void;
}> = ({ initialData, onSubmit, onCancel }) => {
  const [permName, setPermName] = useState(initialData?.perm_name || "");
  const [description, setDescription] = useState(
    initialData?.description || "",
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (permName.trim()) {
      onSubmit({
        perm_name: permName.trim(),
        description: description.trim() || undefined,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Permission Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={permName}
          onChange={(e) => setPermName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., users.create, reports.view, settings.edit"
          autoFocus
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Describe what this permission allows"
          rows={2}
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
          disabled={!permName.trim()}
        >
          {initialData ? "Update" : "Create"} Permission
        </button>
      </div>
    </form>
  );
};

export default PermissionMatrixSettings;
